import { ContentfulLivePreviewProvider } from '@contentful/live-preview/react';
import { ThemeProvider } from '@mui/material';
import CssBaseline from '@mui/material/CssBaseline';
import { sendGTMEvent } from '@next/third-parties/google';
import { RollbarContext, Provider as RollbarProvider } from '@rollbar/react';
import type { AppProps } from 'next/app';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { createEmotionSsrAdvancedApproach } from 'tss-react/next/pagesDir';
import { useStore } from 'zustand';

import { getFeatureFlags } from '@/clients/ecomm/featureFlagClient';
import HTMLHead from '@/components/Common/HTMLHead';
import Cart from '@/components/SunrunShop/Cart/Cart';
import { AppStoreProvider } from '@/data-store/appStoreProvider';
import type { FlagType } from '@/data-store/slices/globalSlice';
import { createAppBoundStore } from '@/data-store/store';
import { rollbarConfig } from '@/rollbar/config';
import { SunrunTheme } from '@/styles/theme';
import { captureAttributionParams } from '@/utils/attribution/attribution';

import '@/styles/globals.css';

type AddProvidersType = {
  addPreview: boolean;
  isStaticPath: boolean;
  initialProps: { featureFlags: FlagType };
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  pageProps: { pageEntry: any; preview: boolean; pageContent: any };
} & Pick<AppProps, 'Component'>;

const appStore = createAppBoundStore();

const addProviders = ({
  Component,
  pageProps,
  addPreview,
  isStaticPath,
  initialProps = { featureFlags: {} },
}: AddProvidersType) => {
  const pageContent = pageProps?.pageContent?.fields;
  const shouldLoadAbTasty =
    pageContent?.intellimizeSnippetEnabled === undefined
      ? true
      : pageContent.intellimizeSnippetEnabled;

  // biome-ignore lint/suspicious/noImplicitAnyLet: Automated migration to Biome
  let location;

  if (isStaticPath) {
    location = typeof window !== 'undefined' ? window?.location?.pathname : '';
  } else {
    location = pageContent?.path;
  }

  const { setFeatureFlags } = useStore(
    appStore,
    (state) => state.global.actions
  );

  if (initialProps.featureFlags) {
    setFeatureFlags(initialProps.featureFlags);
  }

  const mainContent = (
    <RollbarContext context={location}>
      {/* Top level hidden field for Journaya to populate leadId token */}
      {/** biome-ignore lint/correctness/useUniqueElementIds: Automated migration to Biome */}
      <input type='hidden' id='leadid_token' name='universal_leadid' />
      <AppStoreProvider store={appStore}>
        <Cart />
        <Component {...pageProps} />
      </AppStoreProvider>
    </RollbarContext>
  );

  const withTheme = (
    <ThemeProvider theme={SunrunTheme}>
      <CssBaseline />
      <HTMLHead
        seoFields={pageContent?.seoFields}
        currentPath={location}
        shouldLoadAbTasty={shouldLoadAbTasty}
      />
      {mainContent}
    </ThemeProvider>
  );

  const withOptionalPreview = addPreview ? (
    <ContentfulLivePreviewProvider
      locale='en-US'
      enableLiveUpdates={pageProps.preview}
    >
      {withTheme}
    </ContentfulLivePreviewProvider>
  ) : (
    withTheme
  );

  return (
    <RollbarProvider config={rollbarConfig}>
      {withOptionalPreview}
    </RollbarProvider>
  );
};

interface MyAppProps extends AppProps {
  // biome-ignore lint/suspicious/noExplicitAny: Automated migration to Biome
  pageProps: { pageEntry: any; preview: boolean; pageContent: any };
}

function MyApp({ Component, pageProps }: MyAppProps) {
  const router = useRouter();
  const [flags, setFlags] = useState<FlagType>();
  const [fetchedFlags, setFetchedFlags] = useState<FlagType>();

  useEffect(() => {
    getFeatureFlags().then((data) => {
      setFetchedFlags(data);
    });
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Automated migration to Biome
  useEffect(() => {
    if (!router.isReady) return;

    const queryParams = router.query;

    if (
      queryParams.featureFlag &&
      queryParams.featureFlagValue &&
      !!fetchedFlags
    ) {
      setFlags({
        ...fetchedFlags,
        [queryParams.featureFlag as string]:
          queryParams.featureFlagValue as string,
      });
    } else {
      setFlags(fetchedFlags);
    }

    if (queryParams.clearFlag === 'true') {
      getFeatureFlags().then((data) => {
        setFlags(data);
      });
    }
  }, [fetchedFlags]);

  useEffect(() => sendGTMEvent({ event: 'optimize.activate' }), []);
  useEffect(() => captureAttributionParams(router.query), [router.query]);

  const isInEcomm = router.pathname.startsWith('/shop/solar');
  const addPreview = pageProps?.preview || false;

  return addProviders({
    Component,
    pageProps,
    addPreview,
    isStaticPath: isInEcomm,
    initialProps: { featureFlags: flags },
  });
}

const { augmentDocumentWithEmotionCache, withAppEmotionCache } =
  createEmotionSsrAdvancedApproach({ key: 'css' });

export default withAppEmotionCache(MyApp);
export { augmentDocumentWithEmotionCache };
